import type { OutputNode } from './outputFiles';
import type { TestPanelRow } from './harnessDisplay';

export type ScoreCheck = {
  id: string;
  passed: boolean;
  reason: string;
};

export function mergeScoreChecks(
  user?: ScoreCheck[] | null,
  llm?: ScoreCheck[] | null,
): ScoreCheck[] {
  const userList = user ?? [];
  const llmList = llm ?? [];
  if (!userList.length && !llmList.length) return [];
  const byId = new Map<string, ScoreCheck>();
  for (const c of userList) {
    if (!c?.id) continue;
    byId.set(c.id, { id: c.id, passed: !!c.passed, reason: c.reason || '' });
  }
  for (const c of llmList) {
    if (!c?.id) continue;
    byId.set(c.id, { id: c.id, passed: !!c.passed, reason: c.reason || '' });
  }
  const order: string[] = [];
  const seen = new Set<string>();
  for (const c of [...llmList, ...userList]) {
    if (!c?.id || seen.has(c.id)) continue;
    seen.add(c.id);
    order.push(c.id);
  }
  return order.map((id) => byId.get(id)!);
}

export function buildRunScoreRows(checks: ScoreCheck[]): TestPanelRow[] {
  return checks.map((c) => ({ name: c.id, passed: c.passed, reason: c.reason || '' }));
}

export type RunScoreRecord = {
  runId: string;
  userScore: number | null;
  llmScore: number | null;
  reviewMd: string;
  checks: ScoreCheck[];
  at: string;
  durationMs: number | null;
  model: string;
  promptTokens: number | null;
};

export type ScoreChartPoint = {
  runId: string;
  label: string;
  model: string;
  score: number;
  promptTokens: number | null;
};

type Acc = {
  userScore: number | null;
  llmScore: number | null;
  reviewMd: string;
  userChecks: ScoreCheck[];
  llmChecks: ScoreCheck[];
  at: string;
  durationMs: number | null;
  model: string;
  promptTokens: number | null;
};

export function llmAssertionSummary(checks: ScoreCheck[]): string {
  if (!checks.length) return '';
  const failed = checks.filter((c) => !c.passed);
  if (!failed.length) return `${checks.length}/${checks.length} passed`;
  return failed.map((c) => (c.reason ? `${c.id}: ${c.reason}` : c.id)).join(' · ');
}

export function shortRunLabel(runId: string): string {
  const m = /^(\d{8})T(\d{2})(\d{2})(\d{2})Z/.exec(runId);
  if (m) return `${m[2]}:${m[3]}:${m[4]}`;
  if (runId.length > 10) return runId.slice(-8);
  return runId;
}

export function assertionTitle(id: string): string {
  const spaced = id.replace(/[-_]+/g, ' ').trim();
  if (!spaced) return id;
  return spaced.replace(/\b[a-z0-9]+/g, (w) => {
    if (/\d/.test(w)) return w.toUpperCase();
    return w[0].toUpperCase() + w.slice(1);
  });
}

export function sortRunsLatestFirst(rows: RunScoreRecord[]): RunScoreRecord[] {
  return [...rows].sort((a, b) => {
    if (a.runId === b.runId) return 0;
    return a.runId < b.runId ? 1 : -1;
  });
}

export function sortRunsOldestFirst(rows: RunScoreRecord[]): RunScoreRecord[] {
  return [...rows].sort((a, b) => {
    if (a.runId === b.runId) return 0;
    return a.runId < b.runId ? -1 : 1;
  });
}

export function shortModelLabel(model: string): string {
  const t = model.trim();
  if (!t) return '—';
  const slash = t.lastIndexOf('/');
  return slash >= 0 ? t.slice(slash + 1) : t;
}

export function formatContextTokens(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

export const MODEL_BAR_TONES = ['bg-accent', 'bg-success', 'bg-warning', 'bg-border'] as const;

export function modelBarTone(model: string, order: string[]): string {
  const key = model.trim() || 'unknown';
  let i = order.indexOf(key);
  if (i < 0) {
    order.push(key);
    i = order.length - 1;
  }
  return MODEL_BAR_TONES[i % MODEL_BAR_TONES.length];
}

export function scoreChartRuns(history: RunScoreRecord[]): ScoreChartPoint[] {
  return sortRunsOldestFirst(history)
    .map((r) => {
      const score = r.userScore ?? r.llmScore;
      if (typeof score !== 'number') return null;
      return {
        runId: r.runId,
        label: shortRunLabel(r.runId),
        model: r.model || '',
        score,
        promptTokens: r.promptTokens,
      };
    })
    .filter((p): p is ScoreChartPoint => p != null);
}

function asChecks(raw: unknown): ScoreCheck[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((c): c is ScoreCheck => !!c && typeof c === 'object' && typeof (c as ScoreCheck).id === 'string')
    .map((c) => ({ id: c.id, passed: !!c.passed, reason: typeof c.reason === 'string' ? c.reason : '' }));
}

function runIdFromPath(path: string[]): string {
  const i = path.indexOf('runs');
  if (i >= 0 && path[i + 1]) return path[i + 1];
  return '';
}

export function runScoreHistoryFromArtifacts(tree: OutputNode[]): RunScoreRecord[] {
  const byRun = new Map<string, Acc>();
  const walk = (nodes: OutputNode[] | undefined, trail: string[]) => {
    for (const n of nodes || []) {
      const path = trail.concat(n.name || '');
      if (n.children) walk(n.children, path);
      if (n.kind !== 'file' || !n.content) continue;
      const runId = runIdFromPath(path);
      if (!runId) continue;
      let data: Record<string, unknown>;
      try {
        data = JSON.parse(n.content) as Record<string, unknown>;
      } catch {
        continue;
      }
      const cur = byRun.get(runId) || {
        userScore: null, llmScore: null, reviewMd: '', userChecks: [], llmChecks: [], at: '', durationMs: null,
        model: '', promptTokens: null,
      };
      if (n.name === 'manifest.json') {
        if (typeof data.duration_ms === 'number') cur.durationMs = data.duration_ms;
        const started = typeof data.started_at === 'string' ? data.started_at : '';
        if (started && started > cur.at) cur.at = started;
        if (typeof data.model === 'string' && data.model.trim()) cur.model = data.model.trim();
        if (typeof data.prompt_tokens === 'number') cur.promptTokens = data.prompt_tokens;
        byRun.set(runId, cur);
        continue;
      }
      if (!path.includes('review') || !n.name.endsWith('.json')) continue;
      const at = typeof data.at === 'string' ? data.at : '';
      if (at && at > cur.at) cur.at = at;
      if (typeof data.score === 'number') {
        cur.userScore = data.score;
        cur.userChecks = asChecks(data.checks);
      }
      if (typeof data.llm_score === 'number') {
        cur.llmScore = data.llm_score;
        cur.reviewMd = typeof data.review_md === 'string' ? data.review_md : cur.reviewMd;
        cur.llmChecks = asChecks(data.checks);
        if (!cur.userChecks.length) cur.userChecks = asChecks(data.user_checks);
      }
      byRun.set(runId, cur);
    }
  };
  walk(tree, []);
  return [...byRun.entries()]
    .map(([runId, a]) => ({
      runId,
      userScore: a.userScore,
      llmScore: a.llmScore,
      reviewMd: a.reviewMd,
      checks: mergeScoreChecks(a.userChecks, a.llmChecks),
      at: a.at,
      durationMs: a.durationMs,
      model: a.model,
      promptTokens: a.promptTokens,
    }))
    .sort((a, b) => (a.runId === b.runId ? 0 : a.runId < b.runId ? 1 : -1));
}

export function canTrainFromScores(opts: {
  userScore: number | null;
  epoch: number;
  running: boolean;
  success?: boolean;
  bestRecorded: number | null;
}): boolean {
  if (opts.userScore == null || opts.userScore >= 10 || opts.epoch >= 5 || opts.running) return false;
  if (opts.success === false) return false;
  if (opts.bestRecorded != null && opts.userScore < opts.bestRecorded) return false;
  return true;
}

export function bestUserScore(history: RunScoreRecord[]): number | null {
  let best: number | null = null;
  for (const r of history) {
    if (typeof r.userScore !== 'number') continue;
    if (best == null || r.userScore > best) best = r.userScore;
  }
  return best;
}

export function scoreTargetRunId(liveRunId: string | null | undefined, history: RunScoreRecord[]): string {
  const live = (liveRunId || '').trim();
  if (live) return live;
  return history[0]?.runId || '';
}

export function unscoredCompletedRunId(
  history: RunScoreRecord[],
  lastRun?: { run_id?: string; status?: string } | null,
): string {
  if (lastRun?.status && lastRun.status !== 'completed') return '';
  const latest = history[0];
  if (latest && latest.userScore == null) return latest.runId;
  const rid = (lastRun?.run_id || '').trim();
  if (!rid) return '';
  const row = history.find((r) => r.runId === rid);
  if (row?.userScore != null) return '';
  return rid;
}

export type LiveRunOverlay = {
  runId: string | null;
  running: boolean;
  elapsedMs: number;
  userScore: number | null;
  llmScore: number | null;
  reviewMd: string;
  checks: ScoreCheck[];
  model?: string;
  promptTokens?: number | null;
};

export function overlayLiveRun(history: RunScoreRecord[], live: LiveRunOverlay): RunScoreRecord[] {
  if (!live.runId) return history;
  const idx = history.findIndex((r) => r.runId === live.runId);
  const next: RunScoreRecord = idx >= 0 ? { ...history[idx] } : {
    runId: live.runId,
    userScore: null,
    llmScore: null,
    reviewMd: '',
    checks: [],
    at: '',
    durationMs: null,
    model: '',
    promptTokens: null,
  };
  if (typeof live.userScore === 'number') next.userScore = live.userScore;
  if (typeof live.llmScore === 'number') next.llmScore = live.llmScore;
  if (live.reviewMd) next.reviewMd = live.reviewMd;
  if (live.checks.length) next.checks = live.checks;
  if (live.running || next.durationMs == null) next.durationMs = live.elapsedMs;
  if (live.model) next.model = live.model;
  if (typeof live.promptTokens === 'number') next.promptTokens = live.promptTokens;
  const merged = idx < 0 ? [next, ...history] : history.map((r, i) => (i === idx ? next : r));
  const ordered = sortRunsLatestFirst(merged);
  if (!live.running) return ordered;
  const liveRow = ordered.find((r) => r.runId === live.runId);
  if (!liveRow) return ordered;
  return [liveRow, ...ordered.filter((r) => r.runId !== live.runId)];
}
